import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Crown, Loader2, RefreshCw, Search, X, Facebook, MessageCircle, Gift, ShieldCheck, Clock, TrendingUp, Wallet, Users,
  Pause, Play, CalendarPlus, UserPlus, Check, AlertTriangle, Settings, BellRing, Phone, Building2,
} from 'lucide-react';
import { API_URL } from '../../config';
import PaymentMethodBadge from '../shared/PaymentMethodBadge';

// ============================================================
// ADMIN - Offres des agents IA (Page Facebook / WhatsApp)
// Tous les clients avec leur offre (essai / abonnement / offert), l'état, la fin, la Page liée ; l'équipe peut arrêter, reprendre,
// prolonger, offrir ; validation des paiements MVola / Orange ; réglage des tarifs. Partie haute fixe, liste qui défile, pagination.
// ============================================================

const api = (path, opts = {}) => fetch(`${API_URL}/api/admin/agent-offers${path}`, {
  ...opts,
  headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}`, 'Content-Type': 'application/json', ...(opts.headers || {}) },
}).then(r => r.json());

const fmtAr = (n) => `${Math.round(n || 0).toLocaleString('fr-FR')} Ar`.replace(/[  ]/g, ' ');
const fmtDay = (iso) => (iso ? new Date(iso).toLocaleDateString('fr-FR') : '—');
const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—');

const CH = {
  facebook: { label: 'Messenger Bot', icon: Facebook, cls: 'text-blue-300 bg-blue-500/15 border-blue-500/30' },
  facebook_cm: { label: 'Messenger + CM', icon: Settings, cls: 'text-orange-200 bg-orange-500/15 border-orange-400/40' },
  whatsapp: { label: 'WhatsApp', icon: MessageCircle, cls: 'text-emerald-300 bg-emerald-500/15 border-emerald-500/30' },
};
const KIND = {
  trial: { label: 'Essai', cls: 'text-teal-200 bg-teal-500/15 border-teal-500/30' },
  paid: { label: 'Payant', cls: 'text-sky-200 bg-sky-500/15 border-sky-500/30' },
  gift: { label: 'Offert', cls: 'text-fuchsia-200 bg-fuchsia-500/15 border-fuchsia-500/30' },
  legacy: { label: 'Ancien', cls: 'text-gray-300 bg-white/10 border-white/20' },
};
const STATUS = {
  active: { label: 'Actif', dot: '#34d399' }, expired: { label: 'Terminé', dot: '#fbbf24' },
  stopped: { label: 'Suspendu', dot: '#fb7185' }, legacy: { label: 'Sans offre', dot: '#94a3b8' },
};
const STATUS_FILTERS = [
  { key: '', label: 'Tous' }, { key: 'to_setup', label: '🛠 À configurer' }, { key: 'active', label: 'Actifs' }, { key: 'expiring', label: 'Bientôt finis' }, { key: 'trial_expired', label: '🔒 Essais terminés à relancer' }, { key: 'free', label: 'Emplacements libres' },
  { key: 'expired', label: 'Terminés' }, { key: 'stopped', label: 'Suspendus' }, { key: 'legacy', label: 'Anciens sans offre' },
];
const SORTS = [{ key: 'expires', label: 'Fin la plus proche' }, { key: 'recent', label: 'Plus récents' }, { key: 'name', label: 'Nom' }];
const PAGE_SIZES = [10, 25, 50, 100];

function Kpi({ icon: Icon, label, value, sub, tone, onClick }) {
  return (
    <button type="button" onClick={onClick} disabled={!onClick}
      className={`text-left rounded-xl bg-white/5 border border-white/10 px-3 py-2 ${onClick ? 'hover:bg-white/10 transition cursor-pointer' : 'cursor-default'}`}>
      <div className="flex items-center gap-1.5 text-[11px] text-gray-400"><Icon className={`w-3.5 h-3.5 ${tone}`} /> {label}</div>
      <div className="text-lg md:text-xl font-bold leading-tight truncate">{value}</div>
      {sub && <div className="text-[11px] text-gray-500 truncate">{sub}</div>}
    </button>
  );
}

function Pager({ page, pageCount, pageSize, total, onPage, onSize }) {
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);
  const nums = [];
  for (let i = 1; i <= pageCount; i++) {
    if (i === 1 || i === pageCount || Math.abs(i - page) <= 1) nums.push(i);
    else if (nums[nums.length - 1] !== '…') nums.push('…');
  }
  const btn = 'px-2.5 py-1.5 rounded-lg text-xs border transition disabled:opacity-30';
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 text-xs text-gray-400">
      <span>{from}–{to} sur {total}</span>
      <div className="flex items-center gap-1.5">
        <button className={`${btn} bg-white/5 border-white/10 hover:bg-white/10`} disabled={page <= 1} onClick={() => onPage(page - 1)}>‹ Précédent</button>
        {nums.map((n, i) => n === '…'
          ? <span key={`e${i}`} className="px-1">…</span>
          : <button key={n} onClick={() => onPage(n)} className={`${btn} ${n === page ? 'bg-amber-500/25 border-amber-400/60 text-white' : 'bg-white/5 border-white/10 hover:bg-white/10'}`}>{n}</button>)}
        <button className={`${btn} bg-white/5 border-white/10 hover:bg-white/10`} disabled={page >= pageCount} onClick={() => onPage(page + 1)}>Suivant ›</button>
      </div>
      <label className="flex items-center gap-2">Par page
        <select value={pageSize} onChange={e => onSize(Number(e.target.value))} className="bg-slate-800 border border-white/10 rounded-lg px-2 py-1 text-xs text-white">
          {PAGE_SIZES.map(n => <option key={n} value={n}>{n}</option>)}
        </select>
      </label>
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl bg-slate-900 border border-white/15 shadow-2xl p-5 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-3">
          <h3 className="font-bold text-white">{title}</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10 text-gray-400"><X className="w-4 h-4" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

const inputCls = 'w-full bg-black/30 border border-white/15 focus:border-amber-400/70 rounded-xl px-3 py-2 text-sm text-white outline-none';
const Primary = ({ busy, children, ...p }) => (
  <button {...p} disabled={busy || p.disabled} className="w-full px-4 py-2.5 rounded-xl text-sm font-semibold bg-gradient-to-r from-amber-500 to-orange-600 hover:opacity-90 disabled:opacity-50 text-white flex items-center justify-center gap-2">
    {busy && <Loader2 className="w-4 h-4 animate-spin" />}{children}
  </button>
);

// ------------------------------------------------------------ fenêtres d'action
function ReasonModal({ title, label, cta, onSubmit, onClose }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const go = async () => {
    if (!text.trim()) { setErr('La raison est obligatoire : elle est envoyée au client.'); return; }
    setBusy(true); setErr('');
    const r = await onSubmit(text.trim());
    if (r && r.success === false) { setErr(r.message || 'Erreur'); setBusy(false); } else onClose();
  };
  return (
    <Modal title={title} onClose={onClose}>
      <label className="text-xs text-gray-400 block">{label}</label>
      <textarea value={text} onChange={e => setText(e.target.value)} rows={3} className={inputCls} autoFocus />
      {err && <p className="text-sm text-rose-300">{err}</p>}
      <Primary busy={busy} onClick={go}>{cta}</Primary>
    </Modal>
  );
}

function DeleteModal({ row, onSubmit, onClose }) {
  const [word, setWord] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const go = async () => {
    setBusy(true); setErr('');
    const r = await onSubmit();
    if (r && r.success === false) { setErr(r.message || 'Erreur'); setBusy(false); } else onClose();
  };
  return (
    <Modal title="Supprimer définitivement cette offre" onClose={onClose}>
      <div className="rounded-xl bg-rose-500/10 border border-rose-400/40 p-3 text-sm text-rose-100">
        ⚠️ <b>Action irréversible</b>, réservée au Superadmin. L'offre suspendue de <b>{row.contact_name || row.user_name || row.user_email}</b> ({row.channel === 'whatsapp' ? 'WhatsApp' : row.channel === 'facebook_cm' ? 'Messenger + CM' : 'Messenger Bot'}) disparaît pour toujours. Les paiements déjà enregistrés sont conservés.
      </div>
      <label className="text-xs text-gray-400 block">Pour confirmer, tapez <b className="text-white">SUPPRIMER</b></label>
      <input value={word} onChange={e => setWord(e.target.value)} className={inputCls} autoFocus />
      {err && <p className="text-sm text-rose-300">{err}</p>}
      <button onClick={go} disabled={busy || word.trim().toUpperCase() !== 'SUPPRIMER'} className="w-full px-4 py-2.5 rounded-xl text-sm font-semibold bg-gradient-to-r from-rose-600 to-red-700 hover:opacity-90 disabled:opacity-40 text-white flex items-center justify-center gap-2">{busy && <Loader2 className="w-4 h-4 animate-spin" />}Supprimer définitivement</button>
    </Modal>
  );
}

function DaysModal({ title, label, defaultDays, allowNegative, cta, onSubmit, onClose }) {
  const [days, setDays] = useState(defaultDays);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const go = async () => {
    setBusy(true); setErr('');
    const r = await onSubmit(Number(days));
    if (r && r.success === false) { setErr(r.message || 'Erreur'); setBusy(false); } else onClose();
  };
  return (
    <Modal title={title} onClose={onClose}>
      <label className="text-xs text-gray-400 block">{label}</label>
      <div className="flex flex-wrap gap-2">
        {[7, 15, 30, 90].map(d => <button key={d} onClick={() => setDays(d)} className={`px-3 py-1 rounded-full text-xs border ${Number(days) === d ? 'bg-amber-500/25 border-amber-400/60' : 'bg-white/5 border-white/10 hover:bg-white/10'}`}>{d} j</button>)}
      </div>
      <input type="number" value={days} onChange={e => setDays(e.target.value)} min={allowNegative ? -365 : 1} max={365} className={inputCls} />
      {err && <p className="text-sm text-rose-300">{err}</p>}
      <Primary busy={busy} onClick={go}>{cta}</Primary>
    </Modal>
  );
}

function GrantModal({ preset, defaultPrices, onClose, onDone }) {
  const [q, setQ] = useState('');
  const [users, setUsers] = useState([]);
  const [user, setUser] = useState(preset?.user_id ? { id: preset.user_id, name: preset.user_name, email: preset.user_email } : null);
  const [channel, setChannel] = useState(preset?.channel || 'facebook');
  const [kind, setKind] = useState(preset ? 'paid' : 'gift');
  const [days, setDays] = useState(30);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => {
    if (user || q.trim().length < 2) { setUsers([]); return undefined; }
    const t = setTimeout(async () => { const r = await api(`/users?q=${encodeURIComponent(q.trim())}`); if (r.success) setUsers(r.users); }, 300);
    return () => clearTimeout(t);
  }, [q, user]);
  const go = async () => {
    if (!user) { setErr('Choisissez un client.'); return; }
    setBusy(true); setErr('');
    const r = await api('/grant', { method: 'POST', body: JSON.stringify({ user_id: user.id, channel, kind, days: Number(days), client_id: preset?.client_id || '', note }) });
    if (r.success) { onDone(); onClose(); } else { setErr(r.message || 'Erreur'); setBusy(false); }
  };
  return (
    <Modal title={preset ? `Attribuer une offre — ${preset.page_name || 'connexion existante'}` : 'Offrir / attribuer une offre'} onClose={onClose}>
      {user ? (
        <div className="flex items-center justify-between gap-2 rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm">
          <span className="truncate"><b>{user.name || user.email}</b>{user.name ? <span className="text-gray-400"> · {user.email}</span> : null}</span>
          {!preset && <button onClick={() => setUser(null)} className="text-gray-400 hover:text-white"><X className="w-4 h-4" /></button>}
        </div>
      ) : (
        <div className="relative">
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Chercher un client (nom, email, téléphone)…" className={inputCls} autoFocus />
          {users.length > 0 && (
            <div className="absolute z-10 left-0 right-0 mt-1 max-h-56 overflow-auto rounded-xl bg-slate-800 border border-white/15 shadow-xl">
              {users.map(u => <button key={u.id} onClick={() => { setUser(u); setUsers([]); }} className="w-full text-left px-3 py-2 text-sm hover:bg-white/10"><b>{u.name || u.email}</b><span className="block text-xs text-gray-400">{u.email}</span></button>)}
            </div>
          )}
        </div>
      )}
      {!preset && (
        <div className="grid grid-cols-3 gap-2">
          {['facebook', 'facebook_cm', 'whatsapp'].map(c => <button key={c} onClick={() => setChannel(c)} className={`rounded-xl px-3 py-2 text-sm border ${channel === c ? 'bg-white/15 border-white/50' : 'bg-white/5 border-white/10 hover:bg-white/10'}`}>{CH[c].label}</button>)}
        </div>
      )}
      <div>
        <div className="text-xs text-gray-400 mb-1.5">Type</div>
        <div className="flex flex-wrap gap-2">
          {[['paid', 'Payant (compté dans le revenu)'], ['gift', 'Offert'], ['trial', 'Essai']].map(([k, l]) => (
            <button key={k} onClick={() => setKind(k)} className={`px-3 py-1.5 rounded-full text-xs border ${kind === k ? 'bg-amber-500/25 border-amber-400/60' : 'bg-white/5 border-white/10 hover:bg-white/10'}`}>{l}</button>
          ))}
        </div>
        {kind === 'paid' && <p className="text-[11px] text-gray-500 mt-1">Tarif compté : {fmtAr(defaultPrices?.[channel])} par mois.</p>}
      </div>
      <div>
        <div className="text-xs text-gray-400 mb-1.5">Durée (jours)</div>
        <div className="flex flex-wrap gap-2 mb-2">{[7, 30, 90, 180, 365].map(d => <button key={d} onClick={() => setDays(d)} className={`px-3 py-1 rounded-full text-xs border ${Number(days) === d ? 'bg-amber-500/25 border-amber-400/60' : 'bg-white/5 border-white/10 hover:bg-white/10'}`}>{d}</button>)}</div>
        <input type="number" value={days} onChange={e => setDays(e.target.value)} min={1} max={730} className={inputCls} />
      </div>
      <input value={note} onChange={e => setNote(e.target.value)} placeholder="Note interne (facultatif)" className={inputCls} />
      {err && <p className="text-sm text-rose-300">{err}</p>}
      <Primary busy={busy} onClick={go}><UserPlus className="w-4 h-4" /> Attribuer</Primary>
    </Modal>
  );
}

// ------------------------------------------------------------ onglet Abonnements
function daysBadge(r) {
  if (r.status !== 'active' || r.days_left == null) return null;
  const d = r.days_left;
  const cls = d <= 3 ? 'text-amber-300' : 'text-gray-200';
  return <span className={`text-xs ${cls}`}>{d} j restant{d > 1 ? 's' : ''}</span>;
}

function SubsTab({ prices, setKpis, onChangedOrders, onOpenSetup, isSuper }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [qLive, setQLive] = useState('');
  const [q, setQ] = useState('');
  const [channel, setChannel] = useState('');
  const [status, setStatus] = useState('');
  const [kind, setKind] = useState('');
  const [sort, setSort] = useState('expires');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [modal, setModal] = useState(null);
  const [flash, setFlash] = useState('');
  const seq = useRef(0);

  useEffect(() => { const t = setTimeout(() => { setQ(qLive); setPage(1); }, 350); return () => clearTimeout(t); }, [qLive]);

  const load = useCallback(async () => {
    const my = ++seq.current;
    setLoading(true); setError('');
    try {
      const p = new URLSearchParams({ q, channel, status, kind, sort, page, per_page: pageSize });
      const r = await api(`/overview?${p.toString()}`);
      if (my !== seq.current) return;
      if (!r.success) throw new Error(r.message || 'Erreur');
      setRows(r.rows); setTotal(r.total); setKpis(r.kpis, r.prices);
    } catch (e) { if (my === seq.current) setError(e.message || 'Erreur de chargement'); } finally { if (my === seq.current) setLoading(false); }
  }, [q, channel, status, kind, sort, page, pageSize, setKpis]);
  useEffect(() => { load(); }, [load]);

  const setF = (setter) => (v) => { setter(v); setPage(1); };
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const act = async (path, body) => { const r = await api(path, { method: 'POST', body: JSON.stringify(body || {}) }); if (r.success) { load(); onChangedOrders(); } return r; };

  const chip = (active, onClick, label, key) => (
    <button key={key || label} onClick={onClick} className={`shrink-0 px-3 py-1 rounded-full text-xs border transition ${active ? 'bg-amber-500/25 border-amber-400/60 text-white' : 'bg-white/5 border-white/10 text-gray-300 hover:bg-white/10'}`}>{label}</button>
  );

  return (
    <>
      <div className="flex-shrink-0 rounded-2xl bg-white/5 border border-white/10 p-3 space-y-2.5">
        <div className="flex flex-wrap items-center gap-2.5">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={qLive} onChange={e => setQLive(e.target.value)} placeholder="Rechercher un client, un email, une Page…"
              className="w-full bg-black/30 border border-white/10 rounded-xl pl-9 pr-3 py-1.5 text-sm placeholder-gray-500 focus:outline-none focus:border-amber-400" />
          </div>
          <select value={sort} onChange={e => setF(setSort)(e.target.value)} className="bg-black/40 border border-white/10 rounded-xl px-3 py-1.5 text-sm">
            {SORTS.map(s => <option key={s.key} value={s.key}>Trier : {s.label}</option>)}
          </select>
          <button onClick={() => setModal({ type: 'grant' })} className="px-3.5 py-1.5 rounded-xl bg-gradient-to-r from-amber-500 to-orange-600 text-sm font-semibold flex items-center gap-2"><UserPlus className="w-4 h-4" /> Offrir une offre</button>
        </div>
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1">
          {chip(!channel, () => setF(setChannel)(''), 'Tous les canaux')}
          {chip(channel === 'facebook', () => setF(setChannel)('facebook'), 'Messenger Bot')}
          {chip(channel === 'facebook_cm', () => setF(setChannel)('facebook_cm'), 'Messenger + CM')}
          {chip(channel === 'whatsapp', () => setF(setChannel)('whatsapp'), 'WhatsApp')}
          <span className="w-px h-5 bg-white/15 mx-1 shrink-0" />
          {chip(!kind, () => setF(setKind)(''), 'Tous types')}
          {['trial', 'paid', 'gift'].map(k => chip(kind === k, () => setF(setKind)(k), KIND[k].label, k))}
        </div>
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1">
          {STATUS_FILTERS.map(s => chip(status === s.key, () => setF(setStatus)(s.key), s.label, s.key || 'all'))}
        </div>
      </div>
      {error && <p className="flex-shrink-0 text-sm text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-xl px-4 py-2">{error}</p>}
      {flash && <p onClick={() => setFlash('')} className="flex-shrink-0 text-sm text-emerald-200 bg-emerald-500/10 border border-emerald-500/30 rounded-xl px-4 py-2 cursor-pointer">{flash}</p>}

      <div className="flex-1 min-h-0 overflow-auto rounded-2xl border border-white/10 bg-white/[0.02]">
        <div className="hidden lg:grid grid-cols-[minmax(0,2fr)_minmax(0,1.5fr)_80px_100px_120px_100px_minmax(260px,1.3fr)] gap-3 px-4 py-2.5 text-[11px] uppercase tracking-wide text-gray-400 border-b border-white/10 bg-slate-900 sticky top-0 z-10">
          <span>Client</span><span>Page / numéro</span><span>Type</span><span>Statut</span><span>Fin</span><span className="text-right">Tarif</span><span className="text-right">Actions</span>
        </div>
        {loading && rows.length === 0 ? (
          <div className="py-16 flex flex-col items-center gap-2 text-sm text-gray-400"><Loader2 className="w-6 h-6 animate-spin" /> Chargement…</div>
        ) : rows.length === 0 ? (
          <p className="py-14 text-center text-sm text-gray-400">Aucune offre ne correspond à ces filtres.</p>
        ) : rows.map(r => {
          const Ch = CH[r.channel] || CH.facebook;
          const st = STATUS[r.status] || STATUS.legacy;
          const kd = KIND[r.kind] || KIND.legacy;
          return (
            <div key={r.id} className={`grid lg:grid-cols-[minmax(0,2fr)_minmax(0,1.5fr)_80px_100px_120px_100px_minmax(260px,1.3fr)] gap-x-3 gap-y-2 px-4 py-3 items-center border-b border-white/5 hover:bg-white/[0.04] transition ${loading ? 'opacity-60' : ''}`}>
              <span className="min-w-0 flex items-center gap-3">
                <span className="w-9 h-9 shrink-0 rounded-full bg-gradient-to-br from-amber-500/40 to-orange-500/40 flex items-center justify-center font-bold text-sm">{(r.user_name || r.user_email || '?').charAt(0).toUpperCase()}</span>
                <span className="min-w-0"><span className="block font-semibold truncate">{r.contact_name || r.user_name || r.user_email || r.user_id}{r.to_setup && <button onClick={() => onOpenSetup && onOpenSetup(r)} title={r.channel === 'whatsapp' ? 'Ouvrir SendPulse Connexion avec le formulaire déjà rempli' : 'Ouvrir Bot Studio sur cette Page'} className="ml-2 align-middle text-[10px] px-2 py-0.5 rounded-full bg-rose-500/30 border border-rose-400/60 text-rose-100 font-bold animate-pulse hover:bg-rose-500/50 hover:scale-105 transition">🛠 À CONFIGURER →</button>}</span><span className="block text-xs text-gray-400 truncate">{r.user_email}</span>
                  {(r.contact_phone || r.business_name) && <span className="block text-xs text-amber-200/90 truncate">{r.contact_phone && <><Phone className="inline w-3 h-3 -mt-0.5" /> {r.contact_phone}</>}{r.contact_phone && r.business_name ? ' · ' : ''}{r.business_name && <><Building2 className="inline w-3 h-3 -mt-0.5" /> {r.business_name}</>}</span>}</span>
              </span>
              <span className="min-w-0 flex items-center gap-2">
                <span className={`shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[11px] ${Ch.cls}`}>{React.createElement(Ch.icon, { className: "w-3 h-3" })} {Ch.label}</span>
                <span className="truncate text-sm text-gray-200">{r.page_name || (r.free ? <span className="text-emerald-300">Emplacement libre</span> : <span className="text-gray-500">—</span>)}</span>
                {r.bot_active != null && <span title={r.bot_active ? 'Agent actif' : 'Agent en pause'} className={`shrink-0 w-2 h-2 rounded-full ${r.bot_active ? 'bg-emerald-400 animate-pulse' : 'bg-gray-500'}`} />}
              </span>
              <span><span className={`inline-block px-2 py-0.5 rounded-md border text-[11px] ${kd.cls}`}>{kd.label}</span></span>
              <span className="flex items-center gap-1.5 text-sm"><span className="w-2 h-2 rounded-full" style={{ background: st.dot }} />{st.label}</span>
              <span className="text-sm leading-tight">{r.expires_at ? <><span className="block">{fmtDay(r.expires_at)}</span>{daysBadge(r)}</> : <span className="text-gray-500">—</span>}</span>
              <span className="lg:text-right text-sm tabular-nums text-gray-200">{r.price_ar ? fmtAr(r.price_ar) : <span className="text-gray-500">—</span>}</span>
              <span className="flex flex-wrap lg:justify-end gap-1.5">
                {r.row === 'legacy' && <button onClick={() => setModal({ type: 'grant', preset: r })} className="px-2.5 py-1.5 rounded-lg text-xs bg-amber-500/20 border border-amber-400/40 hover:bg-amber-500/30 flex items-center gap-1"><UserPlus className="w-3.5 h-3.5" /> Attribuer une offre</button>}
                {r.row === 'sub' && (r.status === 'active' || r.status === 'expired') && r.kind !== 'gift' && (
                  <button onClick={async () => { const x = await act(`/subs/${r.id}/remind`, {}); setFlash(x.success ? 'Rappel de paiement envoyé dans l\'app du client ✓' : (x.message || 'Erreur')); }}
                    title="Envoie dans l'app un message invitant le client à payer" className="px-2.5 py-1.5 rounded-lg text-xs bg-sky-500/15 border border-sky-400/30 text-sky-200 hover:bg-sky-500/25 flex items-center gap-1"><BellRing className="w-3.5 h-3.5" /> Rappel</button>
                )}
                {r.row === 'sub' && r.status === 'active' && <>
                  <button onClick={() => setModal({ type: 'extend', row: r })} className="px-2.5 py-1.5 rounded-lg text-xs bg-white/10 hover:bg-white/20 flex items-center gap-1"><CalendarPlus className="w-3.5 h-3.5" /> Prolonger</button>
                  <button onClick={() => setModal({ type: 'stop', row: r })} className="px-2.5 py-1.5 rounded-lg text-xs bg-rose-500/15 border border-rose-500/30 text-rose-200 hover:bg-rose-500/25 flex items-center gap-1"><Pause className="w-3.5 h-3.5" /> Arrêter</button>
                </>}
                {r.row === 'sub' && r.status === 'stopped' && <button onClick={() => act(`/subs/${r.id}/resume`, {})} className="px-2.5 py-1.5 rounded-lg text-xs bg-emerald-500/20 border border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/30 flex items-center gap-1"><Play className="w-3.5 h-3.5" /> Reprendre</button>}
                {isSuper && r.row === 'sub' && r.status === 'stopped' && <button onClick={() => setModal({ type: 'delete', row: r })} title="Suppression définitive (Superadmin)" className="px-2.5 py-1.5 rounded-lg text-xs bg-rose-600/25 border border-rose-500/50 text-rose-100 hover:bg-rose-600/40 flex items-center gap-1">🗑 Supprimer</button>}
                {r.row === 'sub' && r.status === 'expired' && <button onClick={() => setModal({ type: 'resume', row: r })} className="px-2.5 py-1.5 rounded-lg text-xs bg-emerald-500/20 border border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/30 flex items-center gap-1"><Play className="w-3.5 h-3.5" /> Réactiver</button>}
              </span>
            </div>
          );
        })}
        {rows.some(r => r.row === 'legacy') && (
          <p className="text-[11px] text-gray-500 px-4 py-3">« Ancien » : Page ou numéro connecté avant la mise en place des offres. Rien n'est coupé ; « Attribuer une offre » le met sous abonnement (essai, offert ou payant) pour le suivre ici.</p>
        )}
      </div>

      <div className="flex-shrink-0">
        <Pager page={Math.min(page, pageCount)} pageCount={pageCount} pageSize={pageSize} total={total} onPage={setPage} onSize={(n) => { setPageSize(n); setPage(1); }} />
      </div>

      {modal?.type === 'stop' && <ReasonModal title="Arrêter cet abonnement" label="Raison (envoyée au client, l'agent IA de la Page est coupé)" cta="Arrêter l'abonnement" onClose={() => setModal(null)}
        onSubmit={(reason) => act(`/subs/${modal.row.id}/stop`, { reason })} />}
      {modal?.type === 'extend' && <DaysModal title="Prolonger l'abonnement" label="Nombre de jours à ajouter (négatif pour retirer)" defaultDays={30} allowNegative cta="Prolonger" onClose={() => setModal(null)}
        onSubmit={(days) => act(`/subs/${modal.row.id}/extend`, { days })} />}
      {modal?.type === 'resume' && <DaysModal title="Réactiver après expiration" label="Durée offerte à partir de maintenant (jours)" defaultDays={30} cta="Réactiver" onClose={() => setModal(null)}
        onSubmit={(days) => act(`/subs/${modal.row.id}/resume`, { days })} />}
      {modal?.type === 'delete' && <DeleteModal row={modal.row} onClose={() => setModal(null)}
        onSubmit={async () => { const r = await api(`/subs/${modal.row.id}`, { method: 'DELETE' }); if (r.success) { load(); onChangedOrders(); } return r; }} />}
      {modal?.type === 'grant' && <GrantModal preset={modal.preset} defaultPrices={prices} onClose={() => setModal(null)} onDone={() => { load(); onChangedOrders(); }} />}
    </>
  );
}

// ------------------------------------------------------------ onglet Paiements
function OrdersTab({ onChanged }) {
  const [status, setStatus] = useState('pending');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [rejecting, setRejecting] = useState(null);
  const [error, setError] = useState('');
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api(`/orders?status=${status}`); if (r.success) setOrders(r.orders || []); } finally { setLoading(false); }
  }, [status]);
  useEffect(() => { load(); }, [load]);
  const validate = async (o) => {
    setBusy(o.id); setError('');
    const r = await api(`/orders/${o.id}/validate`, { method: 'POST' });
    setBusy('');
    if (r.success) { load(); onChanged(); } else setError(r.message || 'Erreur');
  };
  const STC = { pending: 'text-amber-300', approved: 'text-emerald-300', rejected: 'text-rose-300' };
  const STL = { pending: 'À valider', approved: 'Validé', rejected: 'Refusé' };
  return (
    <>
      <div className="flex-shrink-0 flex items-center gap-1.5">
        {[['pending', 'À valider'], ['approved', 'Validés'], ['rejected', 'Refusés'], ['all', 'Tous']].map(([k, l]) => (
          <button key={k} onClick={() => setStatus(k)} className={`px-3 py-1 rounded-full text-xs border ${status === k ? 'bg-amber-500/25 border-amber-400/60' : 'bg-white/5 border-white/10 text-gray-300 hover:bg-white/10'}`}>{l}</button>
        ))}
      </div>
      {error && <p className="flex-shrink-0 text-sm text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-xl px-4 py-2">{error}</p>}
      <div className="flex-1 min-h-0 overflow-auto rounded-2xl border border-white/10 bg-white/[0.02]">
        {loading ? <div className="py-14 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
          : orders.length === 0 ? <p className="py-14 text-center text-sm text-gray-400">Aucun paiement dans cette liste.</p>
            : orders.map(o => {
              const Ch = CH[o.channel] || CH.facebook;
              return (
                <div key={o.id} className="px-4 py-3 border-b border-white/5 flex flex-wrap items-center gap-x-5 gap-y-2">
                  <span className="min-w-[180px] flex-1"><b className="block truncate">{o.contact_name || o.user_email || o.user_id}</b><span className="block text-xs text-gray-400 truncate">{o.user_email} · {fmtDateTime(o.created_at)}</span>
                    {(o.contact_phone || o.business_name) && <span className="block text-xs text-amber-200/90 truncate">{o.contact_phone}{o.contact_phone && o.business_name ? ' · ' : ''}{o.business_name}</span>}</span>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[11px] ${Ch.cls}`}>{React.createElement(Ch.icon, { className: "w-3 h-3" })} {Ch.label}</span>
                  <span className="text-sm text-gray-300">{o.target_sub_id ? 'Renouvellement' : `${o.quantity} emplacement${o.quantity > 1 ? 's' : ''}`} · {o.months} mois</span>
                  <span className="font-bold tabular-nums">{fmtAr(o.amount_ar)}</span>
                  <PaymentMethodBadge method={o.method} />
                  <span className="text-xs text-gray-300">réf. <b className="text-white">{o.payment_ref}</b></span>
                  <span className={`text-xs font-semibold ${STC[o.status] || ''}`}>{STL[o.status] || o.status}{o.admin_note ? ` — ${o.admin_note}` : ''}</span>
                  {o.status === 'pending' && (
                    <span className="flex gap-1.5 ml-auto">
                      <button disabled={busy === o.id} onClick={() => validate(o)} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-500/25 border border-emerald-400/50 text-emerald-100 hover:bg-emerald-500/35 flex items-center gap-1 disabled:opacity-50">
                        {busy === o.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Valider</button>
                      <button onClick={() => setRejecting(o)} className="px-3 py-1.5 rounded-lg text-xs bg-rose-500/15 border border-rose-500/30 text-rose-200 hover:bg-rose-500/25 flex items-center gap-1"><X className="w-3.5 h-3.5" /> Refuser</button>
                    </span>
                  )}
                </div>
              );
            })}
      </div>
      {rejecting && <ReasonModal title="Refuser ce paiement" label="Motif du refus (envoyé au client)" cta="Refuser le paiement" onClose={() => setRejecting(null)}
        onSubmit={async (note) => { const r = await api(`/orders/${rejecting.id}/reject`, { method: 'POST', body: JSON.stringify({ note }) }); if (r.success) { load(); onChanged(); } return r; }} />}
    </>
  );
}

// ------------------------------------------------------------ onglet Tarifs
function PricesTab({ prices, onSaved }) {
  const [fb, setFb] = useState(prices?.facebook || 80000);
  const [fbcm, setFbcm] = useState(prices?.facebook_cm || 150000);
  const [wa, setWa] = useState(prices?.whatsapp || 200000);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  useEffect(() => { if (prices) { setFb(prices.facebook); setFbcm(prices.facebook_cm || 150000); setWa(prices.whatsapp); } }, [prices]);
  const save = async () => {
    setBusy(true); setMsg('');
    const r = await api('/prices', { method: 'PUT', body: JSON.stringify({ facebook: Number(fb), facebook_cm: Number(fbcm), whatsapp: Number(wa) }) });
    setBusy(false);
    if (r.success) { setMsg('Tarifs enregistrés ✓'); onSaved(r.prices); } else setMsg(r.message || 'Erreur');
  };
  return (
    <div className="flex-1 min-h-0 overflow-auto">
      <div className="max-w-xl rounded-2xl bg-white/5 border border-white/10 p-5 space-y-4">
        <p className="text-sm text-gray-300">Tarif mensuel appliqué aux <b>nouvelles</b> commandes (les paiements déjà déclarés gardent leur montant). L'essai gratuit est de 7 jours.</p>
        <label className="block text-xs text-gray-400">Messenger Bot — Ar par Page et par mois<input type="number" value={fb} onChange={e => setFb(e.target.value)} className={`${inputCls} mt-1`} /></label>
        <label className="block text-xs text-gray-400">Messenger Bot + Community Manager Studio — Ar par Page et par mois<input type="number" value={fbcm} onChange={e => setFbcm(e.target.value)} className={`${inputCls} mt-1`} /></label>
        <label className="block text-xs text-gray-400">WhatsApp — Ar par mois<input type="number" value={wa} onChange={e => setWa(e.target.value)} className={`${inputCls} mt-1`} /></label>
        <Primary busy={busy} onClick={save}>Enregistrer les tarifs</Primary>
        {msg && <p className="text-sm text-emerald-300">{msg}</p>}
        <p className="text-[11px] text-gray-500 flex items-start gap-1.5"><AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" /> Pensez à mettre à jour les textes de prix dans les présentations si vous changez ces montants.</p>
      </div>
    </div>
  );
}

// ------------------------------------------------------------ page
export default function AgentOffersAdmin({ onOpenSetup, isSuper = false }) {
  const [tab, setTab] = useState('subs');
  const [kpis, setKpisState] = useState(null);
  const [prices, setPrices] = useState(null);
  const [tick, setTick] = useState(0);
  const setKpis = useCallback((k, p) => { setKpisState(k); if (p) setPrices(p); }, []);
  const refresh = useCallback(() => setTick(t => t + 1), []);
  const k = kpis;

  // les indicateurs se rechargent aussi après une action sur un autre onglet
  useEffect(() => {
    if (tick === 0) return;
    (async () => { const r = await api('/overview?per_page=5'); if (r.success) setKpis(r.kpis, r.prices); })();
  }, [tick, setKpis]);

  const TABS = [
    { key: 'subs', label: 'Abonnements', icon: Users },
    { key: 'orders', label: 'Paiements', icon: Wallet, badge: k?.pending_orders },
    { key: 'prices', label: 'Tarifs', icon: Settings },
  ];

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)] lg:h-screen p-3 md:p-5 gap-3 box-border text-white">
      <div className="flex-shrink-0 space-y-3 w-full max-w-7xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl md:text-2xl font-bold flex items-center gap-2.5">
              <span className="w-9 h-9 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center"><Crown className="w-5 h-5" /></span>
              Offres des agents IA
            </h1>
            <p className="text-xs text-blue-200 mt-0.5">Essais, abonnements et paiements des Pages Facebook et des numéros WhatsApp</p>
          </div>
          <button onClick={refresh} className="p-2.5 rounded-xl bg-white/10 hover:bg-white/20 transition" title="Actualiser les indicateurs"><RefreshCw className="w-4 h-4" /></button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-9 gap-2">
          <Kpi icon={Settings} tone="text-rose-300" label="Agents à configurer" value={k ? k.to_setup : '…'} sub="Page connectée, agent pas encore activé" />
          <Kpi icon={TrendingUp} tone="text-emerald-300" label="Revenu mensuel" value={k ? fmtAr(k.mrr_ar) : '…'} sub="abonnements payants actifs" />
          <Kpi icon={ShieldCheck} tone="text-sky-300" label="Payants actifs" value={k ? k.active_paid : '…'} sub={k ? `+ ${k.active_gift} offert${k.active_gift > 1 ? 's' : ''}` : ''} />
          <Kpi icon={Gift} tone="text-teal-300" label="Essais en cours" value={k ? k.active_trial : '…'} sub="7 jours gratuits" />
          <Kpi icon={Clock} tone="text-amber-300" label="Bientôt finis" value={k ? k.expiring : '…'} sub="dans 3 jours ou moins" />
          <Kpi icon={Wallet} tone="text-orange-300" label="Paiements à valider" value={k ? k.pending_orders : '…'} sub={k ? fmtAr(k.pending_amount) : ''} onClick={() => setTab('orders')} />
          <Kpi icon={TrendingUp} tone="text-lime-300" label="Encaissé (30 j)" value={k ? fmtAr(k.revenue_30d) : '…'} sub="paiements validés" />
          <Kpi icon={Pause} tone="text-rose-300" label="Terminés / suspendus" value={k ? `${k.expired} / ${k.stopped}` : '…'} sub={k ? `dont ${k.trial_expired || 0} essai(s) à relancer` : ''} />
          <Kpi icon={Users} tone="text-gray-300" label="Anciens sans offre" value={k ? k.legacy : '…'} sub="connectés avant les offres" />
        </div>

        <div className="flex items-center gap-1.5 border-b border-white/10">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`px-4 py-2 text-sm flex items-center gap-2 border-b-2 -mb-px transition ${tab === t.key ? 'border-amber-400 text-white font-semibold' : 'border-transparent text-gray-400 hover:text-white'}`}>
              <t.icon className="w-4 h-4" /> {t.label}
              {t.badge > 0 && <span className="px-1.5 py-0.5 rounded-full bg-amber-500 text-[10px] font-bold text-black">{t.badge}</span>}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 min-h-0 flex flex-col gap-3 w-full max-w-7xl mx-auto">
        {tab === 'subs' && <SubsTab prices={prices} setKpis={setKpis} onChangedOrders={refresh} onOpenSetup={onOpenSetup} isSuper={isSuper} />}
        {tab === 'orders' && <OrdersTab onChanged={refresh} />}
        {tab === 'prices' && <PricesTab prices={prices} onSaved={setPrices} />}
      </div>
    </div>
  );
}
